import React, { useId } from 'react';
import { cn } from '@/shared/utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /** When true the input fills its container width (default: true) */
  fullWidth?: boolean;
  /** Ref forwarded to the underlying <input> element */
}

// ---------------------------------------------------------------------------
// Input component
// ---------------------------------------------------------------------------
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      fullWidth = true,
      className,
      id: idProp,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = idProp ?? generatedId;
    const errorId = `${id}-error`;
    const hintId = `${id}-hint`;

    const hasError = Boolean(error);

    const inputClasses = cn(
      // Base
      'block rounded-lg border bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400',
      'transition-colors duration-150',
      // Focus
      'focus:outline-none focus:ring-2 focus:ring-offset-0',
      // Icons padding
      leftIcon && 'pl-9',
      rightIcon && 'pr-9',
      // Width
      fullWidth && 'w-full',
      // Error state
      hasError
        ? 'border-danger-400 focus:ring-danger-400 focus:border-danger-400'
        : 'border-slate-300 focus:ring-primary-500 focus:border-primary-500',
      // Disabled state
      disabled && 'bg-slate-50 text-slate-400 cursor-not-allowed',
      className,
    );

    const describedBy = [hasError && errorId, hint && hintId]
      .filter(Boolean)
      .join(' ') || undefined;

    return (
      <div className={cn('flex flex-col gap-1', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={id}
            className="text-sm font-medium text-slate-700"
          >
            {label}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <span
              className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400"
              aria-hidden="true"
            >
              {leftIcon}
            </span>
          )}

          <input
            ref={ref}
            id={id}
            disabled={disabled}
            aria-invalid={hasError ? 'true' : undefined}
            aria-describedby={describedBy}
            className={inputClasses}
            {...rest}
          />

          {rightIcon && (
            <span
              className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400"
              aria-hidden="true"
            >
              {rightIcon}
            </span>
          )}
        </div>

        {hasError && (
          <p id={errorId} role="alert" className="text-xs text-danger-600">
            {error}
          </p>
        )}

        {hint && !hasError && (
          <p id={hintId} className="text-xs text-slate-500">
            {hint}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';

export default Input;
